<!--
  @project E-School Web
  @author Vothana CHY
  @date 2026-07-13
-->

<script lang="ts" setup>
import { ref, onMounted, onUnmounted, computed } from 'vue';

const showButton = ref(false);
const scrollPercent = ref(0);

const handleScroll = () => {
  if (import.meta.client) {
    const scrollTop = window.scrollY;
    const docHeight = document.documentElement.scrollHeight - window.innerHeight;

    if (docHeight > 0) {
      scrollPercent.value = (scrollTop / docHeight) * 100;
    } else {
      scrollPercent.value = 0;
    }

    showButton.value = scrollTop > 300;
  }
};

const scrollToTop = () => {
  if (import.meta.client) {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }
};

onMounted(() => {
  window.addEventListener('scroll', handleScroll, { passive: true });
  handleScroll();
});

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll);
});

// SVG Progress Math
// Radius is 18px, so circumference is 2 * PI * 18 = ~113.1
const strokeDashoffset = computed(() => {
  const circumference = 2 * Math.PI * 18;
  return circumference - (scrollPercent.value / 100) * circumference;
});
</script>

<template>
  <Transition
    enter-active-class="transition duration-500 cubic-bezier(0.34, 1.56, 0.64, 1)"
    enter-from-class="translate-y-12 scale-75 opacity-0"
    enter-to-class="translate-y-0 scale-100 opacity-100"
    leave-active-class="transition duration-300 ease-in-out"
    leave-from-class="translate-y-0 scale-100 opacity-100"
    leave-to-class="translate-y-12 scale-75 opacity-0"
  >
    <div
      v-show="showButton"
      class="fixed right-4 bottom-4 z-50 flex items-center justify-center sm:right-8 sm:bottom-8"
      role="presentation"
    >
      <button
        type="button"
        class="group relative flex h-11 w-11 items-center justify-center rounded-full bg-white text-brand-blue shadow-lg shadow-black/15 transition-all duration-300 hover:-translate-y-1 hover:bg-brand-blue hover:text-white hover:shadow-xl hover:shadow-brand-blue/20 focus:ring-2 focus:ring-brand-blue/50 focus:ring-offset-2 focus:outline-none active:scale-95 sm:h-14 sm:w-14"
        aria-label="Scroll to top"
        @click="scrollToTop"
      >
        <!-- SVG Progress Ring -->
        <svg class="absolute inset-0 h-11 w-11 -rotate-90 sm:h-14 sm:w-14" viewBox="0 0 44 44">
          <!-- Background track -->
          <circle
            class="text-gray-100 transition-colors duration-300 group-hover:text-brand-blue/10"
            cx="22"
            cy="22"
            r="18"
            fill="transparent"
            stroke="currentColor"
            stroke-width="3"
          />
          <!-- Foreground path representing scroll progress -->
          <circle
            class="text-brand-blue transition-colors duration-300 group-hover:text-brand-red"
            cx="22"
            cy="22"
            r="18"
            fill="transparent"
            stroke="currentColor"
            stroke-width="3"
            stroke-linecap="round"
            :stroke-dasharray="2 * Math.PI * 18"
            :stroke-dashoffset="strokeDashoffset"
          />
        </svg>

        <!-- Inner circle with icon -->
        <div class="relative z-10 flex h-8 w-8 items-center justify-center rounded-full bg-transparent sm:h-10 sm:w-10">
          <Icon
            name="lucide:arrow-up"
            class="h-5 w-5 transition-transform duration-300 group-hover:-translate-y-1 group-hover:animate-pulse sm:h-6 sm:w-6"
          />
        </div>
      </button>
    </div>
  </Transition>
</template>

<style scoped>
/* Smooth cubic bezier entry animation */
.cubic-bezier\(0\.34\,\ 1\.56\,\ 0\.64\,\ 1\) {
  transition-timing-function: cubic-bezier(0.34, 1.56, 0.64, 1);
}
</style>
